// --- Enums -------------------------------------------------------------------

export enum EntityType {
  PARENT = 'parent',
  SUBSIDIARY = 'subsidiary',
  ASSOCIATE = 'associate',
  JOINT_VENTURE = 'joint_venture',
}

export enum ConsolidationMethod {
  FULL = 'full',
  PROPORTIONAL = 'proportional',
  EQUITY = 'equity',
  NOT_CONSOLIDATED = 'none',
}

// FCCS-aligned consolidation methods (7 values)
export enum FCCSConsolidationMethod {
  HOLDING = 'holding',
  SUBSIDIARY = 'subsidiary',
  PROPORTIONAL = 'proportional',
  EQUITY = 'equity',
  NOT_CONSOLIDATED = 'not_consolidated',
  INACTIVE = 'inactive',
  DISCONTINUED = 'discontinued',
}

export interface OwnershipTreeNode {
  entity_id: number;
  entity_code: string;
  entity_name: string;
  entity_type: string;
  ownership_pct: number;
  control: boolean;
  control_override: boolean;
  consolidation_method: string;
  method_override: boolean;
  consolidation_pct: number;
  minority_interest_pct: number;
  is_locked: boolean;
  locked_by_name?: string;
  lock_reason?: string;
  period_lock_cascaded?: boolean;
  ownership_id: number | null;
  // --- Prior & change tracking (US-OW-001) — system-calculated, read-only ---
  ownership_pct_prior?: number | null;
  ownership_pct_change?: number | null;
  consolidation_pct_prior?: number | null;
  consolidation_pct_change?: number | null;
  minority_interest_pct_prior?: number | null;
  minority_interest_pct_change?: number | null;
  consolidation_method_prior?: string | null;
  method_change?: boolean | null;
  control_prior?: boolean | null;
  control_change?: boolean | null;
  elimination_pct?: number | null;
  // --- Prior period comparison (existing display fields) ---
  prior_ownership_pct?: number | null;
  prior_control?: boolean | null;
  prior_consolidation_method?: string | null;
  prior_consolidation_pct?: number | null;
  prior_minority_interest_pct?: number | null;
  children: OwnershipTreeNode[];
}

export interface OwnershipRowUpdate {
  id: number;
  ownership_pct?: number;
  control?: boolean;
  control_override?: boolean;
  consolidation_method?: string;
  method_override?: boolean;
}

export enum AccountType {
  ASSET = 'asset',
  LIABILITY = 'liability',
  EQUITY = 'equity',
  REVENUE = 'revenue',
  EXPENSE = 'expense',
  STATISTICAL = 'statistical',
}

export enum PeriodStatus {
  OPEN = 'open',
  CLOSED = 'closed',
  LOCKED = 'locked',
}

export enum PeriodType {
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  ANNUAL = 'annual',
}

export enum PeriodMemberType {
  YEAR = 'year',
  HALF = 'half',
  QUARTER = 'quarter',
  MONTH = 'month',
  ADJUSTMENT = 'adjustment',
}

export enum PeriodRole {
  BASE = 'base',
  ROLLUP = 'rollup',
  ADJUSTMENT = 'adjustment',
}

export enum FiscalYearStatus {
  OPEN = 'open',
  CLOSED = 'closed',
  LOCKED = 'locked',
}

export enum RunStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
  APPROVED = 'approved',
  ROLLED_BACK = 'rolled_back',
}

export enum SkillStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
  SKIPPED = 'skipped',
}

export enum RateType {
  CLOSING = 'closing',
  AVERAGE = 'average',
  HISTORICAL = 'historical',
}

export enum MatchStatus {
  MATCHED = 'matched',
  UNMATCHED = 'unmatched',
  PARTIAL = 'partial',
  DISPUTED = 'disputed',
}

// --- Core Models -------------------------------------------------------------

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  reporting_currency: string;
  fiscal_year_end_month: number;
  fiscal_year_end_day: number;
  created_at: string;
  updated_at: string;
}

export interface User {
  id: string;
  email: string;
  full_name: string;
  role: 'admin' | 'consolidation_manager' | 'accountant' | 'auditor' | 'preparer' | 'reviewer' | 'viewer';
  tenant_id: string;
  tenant?: Tenant;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Entity {
  id: string;
  tenant_id: string;
  code: string;
  name: string;
  entity_type: EntityType;
  parent_id: string | null;
  parent_name?: string;
  ownership_pct: number;
  consolidation_method: ConsolidationMethod;
  has_control?: boolean;
  enable_intercompany?: boolean;
  functional_currency: string;
  country?: string;
  is_active: boolean;
  // FCCS extended properties
  is_base_entity?: boolean;
  consolidation_tier?: number;
  consolidation_percentage?: number;
  voting_rights_pct?: number;
  acquisition_date?: string;
  deconsolidation_date?: string;
  data_load_approach?: 'FCCS_Periodic' | 'FCCS_YTD';
  consolidation_group?: string;
  default_currency_rate_type?: string;
  allow_adjustments?: boolean;
  indirect_ownership_pct?: number;
  legal_name?: string;
  tax_id?: string;
  region?: string;
  segment?: string;
  local_gaap?: string;
  is_shared_member?: boolean;
  security?: string;
  created_at: string;
  updated_at: string;
  children?: Entity[];
}

export interface GroupAccount {
  id: string;
  tenant_id: string;
  code: string;
  name: string;
  account_type: AccountType | null;
  parent_id: string | null;
  is_header: boolean;
  /**
   * Designed sibling order. The column is `sort_order` (`app/models/account.py`) and both
   * `GET /accounts/group` and `/accounts/group/form-members` return it under that name — the old
   * `display_order` field declared here was never sent by any endpoint, so code sorting on it
   * silently fell back to alphabetical.
   */
  sort_order?: number;
  is_active: boolean;
  is_icp_account?: boolean;
  is_mismatch_account?: boolean;
  mismatch_account_id?: string | null;
  data_storage?: string;
  data_type_account?: string;
  // FCCS extended properties
  time_balance?: string;
  variance_reporting?: string;
  consolidation_operator?: string;
  is_consolidation_only?: boolean;
  data_entry_enabled?: boolean;
  exchange_rate_type?: string;
  default_movement_code?: string;
  is_plug_account?: boolean;
  plug_account_id?: string | null;
  is_cicta_account?: boolean;
  cicta_redirection_account_id?: string | null;
  two_pass_calculation?: boolean;
  fs_line_item?: string;
  cf_category?: string;
  cf_adjustment_type?: string;
  sub_classification?: string;
  flow_balance_type?: string;
  // Sirius FCCS extended fields
  alias_default?: string | null;
  valid_for_consolidations?: string | null;
  description?: string | null;
  formula?: string | null;
  formula_description?: string | null;
  uda?: string | null;
  smart_list?: string | null;
  smart_list_id?: number | null;
  hierarchy_type?: string | null;
  enable_for_dynamic_children?: boolean | null;
  num_possible_dynamic_children?: number | null;
  access_granted_to_member_creator?: string | null;
  allow_upper_level_entity_input?: boolean | null;
  process_management_enabled?: boolean | null;
  fccs_uuid?: string | null;
  fccs_data_id?: string | null;
  old_name?: string | null;
  old_unique_name?: string | null;
  skip_value?: string | null;
  source_plan_type?: string | null;
  plan_type_consol?: string | null;
  aggregation_consol?: string | null;
  data_storage_consol?: string | null;
  formula_consol?: string | null;
  solve_order_consol?: string | null;
  formula_description_consol?: string | null;
  plan_type_rates?: string | null;
  aggregation_rates?: string | null;
  data_storage_rates?: string | null;
  formula_rates?: string | null;
  solve_order_rates?: string | null;
  formula_description_rates?: string | null;
  operation?: string | null;
  created_at: string;
  updated_at: string;
  children?: GroupAccount[];
}

export interface EntityAccount {
  id: string;
  entity_id: string;
  entity_name?: string;
  local_code: string;
  local_name: string;
  group_account_id: string | null;
  group_account_code?: string;
  group_account_name?: string;
  mapping_status?: string;
  account_type: AccountType;
  is_mapped: boolean;
  created_at: string;
  updated_at: string;
}

export interface Period {
  id: string;
  tenant_id: string;
  name?: string;
  year: number;
  month: number;
  period_type?: PeriodType;
  start_date: string;
  end_date: string;
  status: PeriodStatus;
  locked_by?: number | null;
  locked_at?: string | null;
  created_at: string;
  updated_at: string;

  // ── v2 additions (period-dimension hierarchy) ───────────────────────────
  fiscal_year_id?: number | null;
  parent_period_id?: number | null;
  code?: string | null;
  period_member_type?: PeriodMemberType | string;
  period_role?: PeriodRole | string;
  quarter?: number | null;
  half?: number | null;
  period_number?: number | null;
  is_default_closed?: boolean;
  needs_reconsolidation?: boolean;
  is_adjustment_period?: boolean;
  locked_reason?: string | null;
  version?: number;
}

export interface PeriodTreeNode {
  id: number;
  code: string | null;
  name: string | null;
  period_member_type: PeriodMemberType | string;
  period_role: PeriodRole | string;
  status: PeriodStatus | string;
  year: number;
  month?: number | null;
  quarter?: number | null;
  half?: number | null;
  start_date: string;
  end_date: string;
  is_default_closed: boolean;
  needs_reconsolidation?: boolean;
  locked_at?: string | null;
  locked_reason?: string | null;
  children: PeriodTreeNode[];
}

export interface FiscalYear {
  id: number;
  tenant_id: number;
  year: number;
  label: string;
  start_date: string;
  end_date: string;
  status: FiscalYearStatus | string;
  is_open: boolean;
  is_current: boolean;
  calendar_type: string;
  comparative_year_id?: number | null;
  num_periods: number;
  locked_by?: number | null;
  locked_at?: string | null;
  locked_reason?: string | null;
  period_counts?: { open: number; closed: number; locked: number; total: number };
  created_at: string;
  updated_at: string;
}

// ── Scenario Dimension ──────────────────────────────────────────────────────

export enum ScenarioType {
  ACTUAL = 'actual',
  BUDGET = 'budget',
  FORECAST = 'forecast',
  WHAT_IF = 'what_if',
  ACTUAL_LY_RATE = 'actual_ly_rate',
  ACTUAL_PLAN_RATE = 'actual_plan_rate',
}

export enum ScenarioDataStorage {
  STORE = 'store',
  DYNAMIC_CALC = 'dynamic_calc',
  LABEL_ONLY = 'label_only',
}

export interface Scenario {
  id: number;
  tenant_id: number;
  code: string;
  name: string;
  description?: string | null;
  scenario_type: ScenarioType | string;
  data_storage: ScenarioDataStorage | string;
  security?: string | null;
  exch_table?: string | null;

  // Hierarchy
  parent_scenario_id?: number | null;
  parent_scenario_code?: string | null;
  base_scenario_id?: number | null;

  // Period window
  start_fiscal_year_id?: number | null;
  end_fiscal_year_id?: number | null;
  start_period_number?: number | null;
  end_period_number?: number | null;
  start_fiscal_year?: { id: number; year: number; label: string } | null;
  end_fiscal_year?: { id: number; year: number; label: string } | null;

  // Workflow
  requires_approval: boolean;
  allows_data_input: boolean;
  allows_consolidation: boolean;
  is_active: boolean;

  created_at: string;
  updated_at: string;
}

export interface ScenarioTreeNode {
  id: number;
  code: string;
  name: string;
  scenario_type?: string | null;
  data_storage: string;
  requires_approval: boolean;
  is_active: boolean;
  parent_scenario_id?: number | null;
  start_fiscal_year_id?: number | null;
  end_fiscal_year_id?: number | null;
  children: ScenarioTreeNode[];
}

export function periodDisplayName(p: Period | PeriodTreeNode): string {
  if (p.name) return p.name;
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const mt = (p as any).period_member_type;
  if (mt === PeriodMemberType.YEAR || mt === 'year') return `FY ${p.year}`;
  if (mt === PeriodMemberType.HALF || mt === 'half') return `HY${(p as any).half} ${p.year}`;
  if (mt === PeriodMemberType.QUARTER || mt === 'quarter') return `Q${(p as any).quarter} ${p.year}`;
  if (mt === PeriodMemberType.ADJUSTMENT || mt === 'adjustment') return `Adj ${p.year}`;
  if ((p as any).period_type === PeriodType.ANNUAL) return `FY ${p.year}`;
  const m = (p as any).month;
  if (m === 0) return `Opening ${p.year}`;
  return `${monthNames[((m || 1) as number) - 1]} ${p.year}`;
}

export interface TrialBalance {
  id: string;
  entity_id: string;
  period_id: string;
  account_id: number;
  debit: number;
  credit: number;
  local_currency_debit: number;
  local_currency_credit: number;
  currency: string;
  source: string;
  status: string;
  uploaded_at?: string | null;
  validated_at?: string | null;
  created_at: string;
  updated_at: string;
  // Joined fields (may not always be present)
  account_code?: string;
  account_name?: string;
  // Optional ICP code carried from TB upload (e.g. FCCS ICP_Code)
  icp_code?: string;
  // Upload metadata
  filename?: string;
  scenario?: string;
  balance_type?: string;
  // Consolidation dimension: 'Total' | 'Parent' | 'NCI'
  member?: string;
  // Flexible FCCS dimension tags: {custom1: "EMEA", custom2: "ProductA", ...}
  dimension_tags?: Record<string, string> | null;
  // Journal-adjusted fields
  adjusted_debit?: number;
  adjusted_credit?: number;
  journal_debit?: number;
  journal_credit?: number;
  journal_count?: number;
  is_journal_only?: boolean;
}

export interface TrialBalanceUpload {
  entity_id: number;
  entity_name: string;
  entity_code: string;
  period_id: number;
  period_label: string;
  scenario: string;
  balance_type: string;
  filename: string | null;
  status: string;
  row_count: number;
  total_debits: number;
  total_credits: number;
  is_balanced: boolean;
  uploaded_at: string | null;
}

export interface NciReportRow {
  account_code: string;
  account_name: string;
  account_type: string;
  total_balance: number;
  parent_portion: number;
  nci_portion: number;
}

export interface NciReport {
  entity_id: number;
  period_id: number;
  ownership_pct: number;
  nci_pct: number;
  pnl_nci: number;
  equity_nci: number;
  cta_nci: number;
  total_nci: number;
  currency: string;
  breakdown: NciReportRow[];
}

export interface NciPipelineStep {
  step: string;
  status: 'completed' | 'skipped' | 'failed' | 'warning';
  message: string;
  duration_ms: number;
}

export interface NciPipelineResult extends NciReport {
  status: 'success' | 'skipped' | 'error';
  message?: string;
  entity_name: string;
  steps_executed: NciPipelineStep[];
  total_duration_ms: number;
  translation_source: 'existing' | 'computed' | 'not_needed';
  cta_computed: boolean;
  cta_amount?: number;
  validation_ok?: boolean;
}

export interface TrialBalanceSummary {
  entity_id: string;
  entity_name: string;
  period_id: string;
  total_debits: number;
  total_credits: number;
  difference: number;
  is_balanced: boolean;
  row_count: number;
  unmapped_accounts: number;
}

export interface Currency {
  id: string;
  tenant_id: string;
  code: string;
  name: string;
  symbol: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TranslatedTrialBalanceRow {
  account_code: string;
  account_name: string;
  account_type: string;        // 'asset' | 'liability' | 'equity' | 'revenue' | 'expense'
  from_currency: string;
  local_debit: number;
  local_credit: number;
  translated_debit: number;
  translated_credit: number;
  rate: number;
  rate_type: string;           // 'average' | 'closing'
}

export interface TrialBalanceTranslationResponse {
  rows: TranslatedTrialBalanceRow[];
  cta_amount: number;
  total_translated_debit: number;
  total_translated_credit: number;
  reporting_currency: string;
  from_currency: string;
  average_rate: number | null;
  closing_rate: number | null;
}

export interface FxRate {
  id: string;
  tenant_id: string;
  period_id: string;
  period_name?: string;
  from_currency: string;
  to_currency: string;
  rate_type: RateType;
  rate: number;
  source?: string;
  created_at: string;
  updated_at: string;
}

export interface IntercompanyTransaction {
  id: string;
  tenant_id: string;
  period_id: string;
  period_name?: string;
  from_entity_id: string;
  from_entity_name?: string;
  to_entity_id: string;
  to_entity_name?: string;
  account_id: string;
  account_name?: string;
  amount: number;
  currency: string;
  is_matched: boolean;
  match_status?: MatchStatus;
  match_difference: number;
  counterpart_id?: string | null;
  description?: string;
  created_at: string;
  updated_at: string;
  // Additional fields returned by /intercompany endpoints
  entity_from_id?: number | string;
  entity_from_name?: string;
  entity_to_id?: number | string;
  entity_to_name?: string;
  transaction_type?: string;
  group_account_code?: string;
  group_account_name?: string;
  transaction_date?: string;
  reference?: string;
  invoice_reference?: string;
  unrealised_profit_amount?: number;
  matched_with_id?: number | string | null;
}

export interface IntercompanySummary {
  total_transactions: number;
  matched_count: number;
  unmatched_count: number;
  partial_count?: number;
  disputed_count?: number;
  total_amount: number | string;
  matched_amount?: number | string;
  unmatched_amount: number | string;
  /** Legs that are linked to a mirror IC line (pair netted for elimination). */
  netted_leg_count?: number;
  /** Legs with no counterparty line yet. */
  open_leg_count?: number;
  open_leg_amount?: number | string;
  netted_rate?: string;
  /** Paired IC legs / 2 for the period (Oracle-style cleared pairs). */
  cleared_pair_count?: number;
}

export interface IntercompanyReconciliationReport {
  period_id: number;
  summary: {
    total_intercompany_exposure: string;
    matched_percentage: string;
    total_matched: number;
    total_unmatched: number;
    unmatched_by_entity_pair: { entity_pair: string; unmatched_count: number; difference: string }[];
    trend_vs_prior_period?: {
      prior_period_id: number | null;
      prior_matched_percentage: string | null;
      prior_total_unmatched: number | null;
    } | null;
  };
  entity_pairs: {
    entity_from_id: number;
    entity_to_id: number;
    entity_from_name: string;
    entity_to_name: string;
    total_from_side_a: string;
    total_from_side_b: string;
    difference: string;
    matched_count: number;
    unmatched_count: number;
    unmatched_items?: { id: number; amount: string; currency: string; status: string; transaction_date: string | null }[];
  }[];
}

export interface IntercompanyEliminationTestResult {
  success: boolean;
  period_id: number;
  source_system: string;
  dry_run: boolean;
  message: string;
  sync: Record<string, unknown>;
  records_processed: number;
  journal_entries_would_be_created: number;
  summary: Record<string, unknown>;
  warnings: string[];
  errors: string[];
}

export interface IcDispute {
  id: number;
  tenant_id: number;
  period_id: number;
  entity_from_id: number;
  entity_to_id: number;
  entity_from_name?: string;
  entity_to_name?: string;
  amount_from: number;
  amount_to: number;
  currency: string;
  transaction_type: string;
  description?: string;
  status: string;
  resolution_deadline?: string;
  proposed_adjustment?: string;
  resolution_notes?: string;
  created_at: string;
  updated_at: string;
}

export interface EntityCodeMapping {
  id: number;
  tenant_id: number;
  source_system: string;
  external_code: string;
  entity_id: number;
  entity_name?: string;
  entity_code?: string;
  created_at: string;
  updated_at: string;
}

export interface ValidateCodesResponse {
  valid: string[];
  invalid: string[];
  mapping: Record<string, number>;
}

export interface JournalEntryLine {
  id: string;
  journal_entry_id: string;
  account_id: string;
  account_code?: string;
  account_name?: string;
  entity_id: string | null;
  entity_name?: string;
  debit_amount: number;
  credit_amount: number;
  description: string;
}

export interface JournalEntry {
  id: string;
  consolidation_run_id: string;
  entry_type: string;
  reference: string;
  description: string;
  skill_name: string;
  is_auto_generated: boolean;
  is_approved: boolean;
  created_at: string;
  lines: JournalEntryLine[];
}

export interface SkillOutput {
  skill_name: string;
  status: SkillStatus;
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;
  summary: Record<string, unknown>;
  errors: string[];
  journal_entries_created: number;
}

export interface SkillExecution {
  skill_name: string;
  display_name: string;
  description: string;
  step_order: number;
  status: SkillStatus;
  progress_pct: number;
  message: string;
}

export interface ConsolidationRun {
  id: string;
  tenant_id: string;
  period_id: string;
  period_name?: string;
  status: RunStatus;
  started_at: string;
  completed_at: string | null;
  initiated_by: string;
  initiated_by_name?: string;
  skill_outputs: SkillOutput[];
  created_at: string;
  updated_at: string;
}

export interface ConsolidationResult {
  run_id: string;
  status: RunStatus;
  skills: SkillExecution[];
  journal_entries: JournalEntry[];
  summary: {
    total_eliminations: number;
    elimination_amount?: number;
    fx_adjustments: number;
    equity_adjustments: number;
    total_journal_entries: number;
  };
}

// --- Financial Statements ----------------------------------------------------

export interface FinancialLineItem {
  account_code: string;
  account_name: string;
  account_type: AccountType;
  is_header: boolean;
  is_total: boolean;
  indent_level: number;
  current_period: number;
  prior_period: number | null;
  variance: number | null;
  variance_pct: number | null;
  entity_breakdown?: Record<string, number>;
}

export interface FinancialStatement {
  statement_type: 'balance_sheet' | 'income_statement' | 'cash_flow';
  period_name: string;
  prior_period_name: string | null;
  entity_name: string;
  currency: string;
  generated_at: string;
  line_items: FinancialLineItem[];
  totals: {
    total_assets?: number;
    total_liabilities?: number;
    total_equity?: number;
    total_revenue?: number;
    total_expenses?: number;
    net_income?: number;
  };
  warnings?: string[];
  unmapped_count?: number;
  unmapped_total?: number;
  raw_unmapped_accounts?: Array<{
    account_code: string;
    account_name: string;
    consolidated_balance: string;
  }>;
}

// --- API Response Types ------------------------------------------------------

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface ApiError {
  detail: string;
  status_code: number;
  /**
   * Field-level errors. TWO shapes reach here: the `{field: [msg, …]}` map most
   * routers return, and the reporting engine's list of
   * `{scope, ref, code, detail}` objects (POV/member failures, which the Excel
   * handoff surfaces). Read it through `formatFieldErrors` — assuming the map
   * shape threw inside an error handler and swallowed the message entirely.
   */
  errors?: Record<string, string[]> | Array<Record<string, unknown> | string>;
  /** Raw axios error code (e.g. 'ECONNABORTED', 'ERR_NETWORK'). Absent for real HTTP errors. */
  code?: string;
  /**
   * True when the request was aborted client-side before the server answered
   * (axios timeout / network drop). The server may still be working — callers
   * must NOT report the operation as failed on this. See the run handler in
   * IntegrationList.tsx.
   */
  is_timeout?: boolean;
}

export interface AuthTokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user: User;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  full_name: string;
  tenant_name?: string;
}

// --- User Management ---------------------------------------------------------

export interface UserCreate {
  email: string;
  password: string;
  full_name: string;
  role: 'admin' | 'consolidation_manager' | 'accountant' | 'auditor' | 'preparer' | 'reviewer' | 'viewer';
  is_active?: boolean;
}

export interface UserUpdate {
  email?: string;
  full_name?: string;
  role?: 'admin' | 'consolidation_manager' | 'accountant' | 'auditor' | 'preparer' | 'reviewer' | 'viewer';
  is_active?: boolean;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

// --- Skill Types -------------------------------------------------------------

export interface SkillInfo {
  name: string;
  display_name: string;
  description: string;
  step_order: number;
  enabled: boolean;
  params: Record<string, unknown>;
}

export interface SkillExecutionRequest {
  skill_name: string;
  params?: Record<string, unknown>;
}

export interface SkillExecutionResponse {
  skill_name: string;
  status: SkillStatus;
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;
  summary: Record<string, unknown>;
  errors: string[];
  journal_entries_created: number;
}

// --- Audit -------------------------------------------------------------------

export interface AuditLog {
  id: number;
  action: string;
  resource_type: string;
  resource_id: number | null;
  user_email: string;
  details: Record<string, any>;
  ip_address: string;
  created_at: string;
}

export interface AuditSummary {
  total_actions: number;
  actions_by_type: { action: string; count: number }[];
  actions_per_user: { user_id: number | null; user_email: string | null; count: number }[];
  actions_per_day: { date: string; count: number }[];
  most_active_resources: { resource_type: string; count: number }[];
}

// --- Tax ---------------------------------------------------------------------

export interface EntityTaxDetail {
  entity_id: number;
  entity_name: string;
  accounting_profit: number;
  add_backs: { description: string; amount: number }[];
  exempt_income: { description: string; amount: number }[];
  taxable_income: number;
  tax_rate: number;
  tax_liability: number;
  effective_tax_rate: number;
  is_qfzp: boolean;
  sbr_eligible: boolean;
}

export interface TaxGroupTotal {
  total_accounting_profit: number;
  total_taxable_income: number;
  total_tax_liability: number;
  group_effective_rate: number;
}

export interface TaxComputeResponse {
  period_id: number;
  run_id?: number | null;
  entity_results: EntityTaxDetail[];
  group_total: TaxGroupTotal;
  success: boolean;
  errors?: string[];
  warnings?: string[];
}

export interface QfzpEntityStatus {
  entity_id: number;
  entity_name: string;
  is_qfzp: boolean;
  qualifying_income: number;
  non_qualifying_income: number;
  de_minimis_pass: boolean;
}

// --- Transfer Pricing --------------------------------------------------------

export interface TPTransaction {
  id: number;
  entity_from: string;
  entity_to: string;
  amount: number;
  currency: string;
  method_applied: string;
  arm_length_range: { q1: number; median: number; q3: number };
  compliance_status: 'compliant' | 'non_compliant' | 'pending';
  adjustment_amount: number;
}

export interface TPSummary {
  total_transactions: number;
  compliant: number;
  non_compliant: number;
  total_adjustment: number;
  by_method: Record<string, number>;
  risk_rating: 'low' | 'medium' | 'high';
}

// --- Anomaly Detection -------------------------------------------------------

export interface Anomaly {
  id: number;
  severity: 'high' | 'medium' | 'low';
  category: 'statistical' | 'variance' | 'benford' | 'ratio' | 'journal';
  entity_name: string;
  account_name: string;
  description: string;
  recommendation: string;
  detected_value: number | null;
  expected_min: number | null;
  expected_max: number | null;
  resolution_status: 'open' | 'acknowledged' | 'false_positive' | 'resolved';
  resolution_notes: string | null;
  created_at: string;
}

export interface AnomalySummary {
  high: number;
  medium: number;
  low: number;
  by_category: Record<string, number>;
  most_affected_entities: { name: string; count: number }[];
  most_affected_accounts: { name: string; count: number }[];
  benford: BenfordAnalysis;
}

export interface BenfordAnalysis {
  digits: { digit: number; expected_pct: number; observed_pct: number; count: number }[];
  chi_squared: number;
  p_value: number;
  significant: boolean;
}

// --- Reports Extended --------------------------------------------------------

export interface DrillDownItem {
  entity_id: number;
  entity_name: string;
  amount: number;
  currency: string;
  pct_of_total: number;
}

export interface ExportRequest {
  report_type: string;
  period_id: number;
  entity_id?: number;
  format: 'xlsx' | 'pdf';
  include_comparative: boolean;
}

export interface TranslationRequest {
  period_id: number;
  to_currency: string;
  statement_type: 'balance_sheet' | 'income_statement';
  entity_id?: number | null;
}

// --- ERP Connector -----------------------------------------------------------

export interface ERPConnector {
  erp_type: string;
  display_name: string;
  status: 'active' | 'setup_required';
}

// --- Notification ------------------------------------------------------------

export interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  severity?: 'info' | 'warning' | 'error' | 'success';
  is_read?: boolean;
  metadata?: Record<string, any>;
  timestamp: string;
  read: boolean;
  created_at?: string;
}

// --- Phase 6 Types -----------------------------------------------------------

export interface ApprovalWorkflow {
  id: string;
  workflow_type: string;
  entity_type: string;
  entity_id: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  requested_by: string;
  approved_by: string | null;
  comments: string;
  created_at: string;
  resolved_at: string | null;
}

export interface NLQueryResponse {
  query: string;
  interpretation: string;
  data: any;
  visualization_hint: string;
  confidence: number;
}

export interface XBRLMapping {
  account_code: string;
  account_name?: string;
  xbrl_element: string;
  taxonomy: string;
}

export interface XBRLValidationResult {
  is_valid: boolean;
  errors: string[];
  warnings: string[];
}

export interface BackgroundTask {
  id: string;
  task_type: string;
  status: string;
  progress_percent: number;
  current_step: string;
  created_at: string;
}

// --- Phase 7 Types -----------------------------------------------------------

export interface SSOProvider {
  id: string;
  provider_type: string;
  provider_name: string;
  enabled: boolean;
}

export interface MFASetup {
  qr_code_uri: string;
  secret: string;
  backup_codes: string[];
}

export interface TaxReturn {
  id: string;
  period_id: string;
  return_type: string;
  status: string;
  return_data: any;
  submitted_at: string | null;
}

// --- Phase 8 Types -----------------------------------------------------------

export interface ESGMetric {
  id: string;
  entity_id: string;
  entity_name?: string;
  category: string;
  metric_name: string;
  value: number;
  unit: string;
  framework: string;
  framework_reference: string;
  period_id?: string;
}

export interface ESGTarget {
  id: string;
  metric_code: string;
  metric_name?: string;
  target_value: number;
  target_year: number;
  baseline_value: number;
  current_value?: number;
  status: string;
}

export interface ESGDashboardData {
  overall_score: number;
  environmental_score: number;
  social_score: number;
  governance_score: number;
  metrics_by_category: Record<string, ESGMetric[]>;
  framework_coverage: { framework: string; coverage_pct: number }[];
  targets: ESGTarget[];
}

export interface ForecastResult {
  id: string;
  entity_id: string;
  entity_name?: string;
  account_code: string;
  account_name?: string;
  forecasted_values: {
    period: string;
    value: number;
    lower_bound: number;
    upper_bound: number;
  }[];
  method: string;
  confidence: number;
}

export interface MarketplaceSkill {
  id: string;
  skill_id: string;
  name: string;
  description: string;
  category: string;
  version: string;
  author?: string;
  is_official: boolean;
  is_certified?: boolean;
  install_count: number;
  rating: number;
  is_installed?: boolean;
  requirements?: string[];
}

export interface AuditBlock {
  id: string;
  block_number: number;
  current_hash: string;
  previous_hash?: string;
  event_type: string;
  entity_type: string;
  user_id?: string;
  user_email?: string;
  data: any;
  timestamp: string;
}

export interface AuditChainStats {
  total_blocks: number;
  first_block_timestamp: string;
  last_block_timestamp: string;
  event_type_distribution: Record<string, number>;
  is_verified: boolean;
  last_verified_at: string | null;
}

export interface ScenarioResult {
  id: string;
  scenario_type: string;
  name?: string;
  description?: string;
  parameters?: Record<string, any>;
  impact_summary: {
    metric: string;
    base_value: number;
    adjusted_value: number;
    delta: number;
    delta_pct: number;
  }[];
  created_at?: string;
}

export interface GaapConversion {
  source_gaap: string;
  target_gaap: string;
  adjustments: {
    rule: string;
    standard_ref: string;
    account: string;
    amount: number;
    description: string;
  }[];
  reconciliation: {
    line_item: string;
    source_amount: number;
    adjustments: number;
    target_amount: number;
  }[];
}

// --- Phase 9 Types -----------------------------------------------------------

export interface DataQualityReport {
  entity_id: number;
  entity_name: string;
  period_id: number;
  score: number;
  grade: string;
  checks_passed: number;
  checks_failed: number;
  checks_total: number;
  results: CheckResult[];
  critical_failures: CheckResult[];
  generated_at: string;
}

export interface CheckResult {
  check_id: string;
  category: string;
  description: string;
  passed: boolean;
  severity: 'critical' | 'high' | 'medium' | 'low';
  details: string;
  suggestion: string;
}

export interface CloseTimeline {
  period_id: number;
  start_date: string;
  predicted_end_date: string;
  confidence: number;
  stages_timeline: { stage: string; estimated_date: string }[];
  bottlenecks: Bottleneck[];
}

export interface Bottleneck {
  entity_id: number;
  entity_name: string;
  stage: string;
  reason: string;
  impact_days: number;
  suggestion: string;
}

export interface EntityReadiness {
  entity_id: number;
  entity_name: string;
  stages: Record<string, boolean>;
  completion_pct: number;
  estimated_completion: string;
}

// --- Phase 10 Types ----------------------------------------------------------

export interface KPIResult {
  kpi_id: string;
  name: string;
  value: number;
  formatted_value: string;
  category: string;
  trend: 'up' | 'down' | 'stable';
  change_pct: number;
  prior_value: number;
}

export interface KPIDashboard {
  period_id: number;
  kpis: KPIResult[];
  categories: Record<string, KPIResult[]>;
}

export interface EntityCloseStatus {
  entity_id: number;
  entity_name: string;
  stages: { stage: string; status: string; started_at: string | null; completed_at: string | null }[];
  completion_pct: number;
}

export interface PeriodCloseStatus {
  period_id: number;
  period_name: string;
  entities: EntityCloseStatus[];
  overall_pct: number;
  entities_complete: number;
  entities_total: number;
}

export interface ConsolidationRule {
  id: number;
  name: string;
  description: string;
  conditions: { field: string; operator: string; value: any }[];
  condition_logic: string;
  actions: { type: string; params: Record<string, any> }[];
  is_active: boolean;
  priority: number;
  last_evaluated_at: string | null;
}

export interface MappingSuggestion {
  entity_account_id: number;
  entity_account_code: string;
  entity_account_name: string;
  suggested_group_account_id: number;
  suggested_group_account_code: string;
  suggested_group_account_name: string;
  confidence: number;
  match_method: string;
}

export interface BudgetEntry {
  id: number;
  entity_id: number;
  period_id: number;
  group_account_id: number;
  budget_type: 'budget' | 'forecast' | 'what_if';
  amount: number;
  currency: string;
  notes: string;
  is_approved: boolean;
  entity_name: string;
  account_name: string;
}

export interface BudgetVariance {
  account_id: number;
  account_name: string;
  budget_amount: number;
  actual_amount: number;
  variance: number;
  variance_pct: number;
}

// --- Phase 11 Types ----------------------------------------------------------

export interface SearchResult {
  type: string;
  id: number;
  title: string;
  subtitle: string;
  highlight: string;
  relevance_score: number;
}

export interface CDCRecord {
  id: number;
  entity_type: string;
  entity_id: string;
  change_type: 'insert' | 'update' | 'delete';
  changed_by: number | null;
  changed_at: string;
  is_synced: boolean;
}

export interface RecurringTemplate {
  id: number;
  name: string;
  description: string;
  entity_id: number | null;
  journal_lines: { account_code: string; debit: number; credit: number; description: string }[];
  frequency: string;
  base_amount: number | null;
  is_auto_apply: boolean;
  application_count: number;
  is_active: boolean;
}

// --- Phase 12 Types ----------------------------------------------------------

export interface WebhookSubscription {
  id: number;
  name: string;
  url: string;
  events: string[];
  status: 'active' | 'inactive' | 'failed';
  consecutive_failures: number;
  last_triggered_at: string | null;
  last_success_at: string | null;
  created_at: string;
}

export interface WebhookDelivery {
  id: number;
  event_type: string;
  response_status: number | null;
  success: boolean;
  duration_ms: number | null;
  attempt: number;
  delivered_at: string;
  error_message: string | null;
}

export interface APIKeyInfo {
  id: number;
  name: string;
  key_prefix: string;
  scopes: string[];
  expires_at: string | null;
  last_used_at: string | null;
  usage_count: number;
  is_active: boolean;
  created_at: string;
}

export interface SecurityCheck {
  id: string;
  category: string;
  name: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: 'pass' | 'fail' | 'warning';
  details: string;
}

export interface SecurityAuditReport {
  total_checks: number;
  passed: number;
  failed: number;
  warnings: number;
  score: number;
  categories: Record<string, SecurityCheck[]>;
  recommendations: string[];
}

export interface PeriodArchive {
  id: number;
  period_id: number;
  period_name: string;
  status: string;
  archived_at: string | null;
  archive_size_bytes: number | null;
  records_archived: Record<string, number> | null;
}

// --- Phase 13 Types ----------------------------------------------------------

export interface SegmentResult {
  segment_name: string;
  external_revenue: number;
  inter_segment_revenue: number;
  segment_profit: number;
  segment_assets: number;
  segment_liabilities: number;
  capex: number;
}

export interface PolicyConflict {
  entity_id: number;
  entity_name: string;
  policy_key: string;
  group_policy: string;
  entity_policy: string;
  severity: 'high' | 'medium' | 'low';
  impact_description: string;
  ifrs_reference: string;
}

export interface RuleSetVersion {
  id: number;
  version_number: number;
  name: string;
  effective_from: string;
  effective_to: string | null;
  is_current: boolean;
  rules_count: number;
  created_at: string;
  approved_at: string | null;
}

export interface MPMResult {
  mpm_id: string;
  name: string;
  value: number;
  formatted_value: string;
  reconciliation: { item: string; amount: number }[];
}

// --- Phase 14 Types ----------------------------------------------------------

export interface OnboardingStep {
  step: number;
  name: string;
  title: string;
  description: string;
  status: 'not_started' | 'in_progress' | 'completed' | 'skipped';
  is_required: boolean;
}

export interface OnboardingProgress {
  current_step: number;
  steps: OnboardingStep[];
  completion_pct: number;
  is_complete: boolean;
}

export interface ImportSession {
  id: number;
  import_type: string;
  status: string;
  file_name: string;
  total_rows: number;
  valid_rows: number;
  error_rows: number;
  column_mapping: Record<string, string> | null;
  detected_columns: string[] | null;
}

export interface StorageUsage {
  total_bytes: number;
  by_type: Record<string, number>;
  by_period: { period_name: string; bytes: number }[];
}

export interface SystemHealth {
  cpu_percent: number;
  memory_percent: number;
  disk_percent: number;
  db_pool_size: number;
  db_pool_available: number;
  redis_connected: boolean;
}

/** Multi-column consolidation review grid (FCCS-style). */
export interface ConsolidationReviewMatrixEntity {
  id: number;
  code: string;
  name: string;
  local_currency: string;
  ownership_pct: number;
  fx_rate_to_reporting: string;
}

export interface ConsolidationReviewMatrixSubcolumn {
  key: string;
  label: string;
}

export interface ConsolidationReviewMatrixColumnGroup {
  key: string;
  label: string;
  subcolumns: ConsolidationReviewMatrixSubcolumn[];
}

export interface ConsolidationReviewMatrixRowEntityValues {
  local_tb: string;
  local_je: string;
  local_standalone: string;
  translated_tb: string;
  translated_je: string;
  translated_standalone: string;
  proportion: string;
  ic_elimination: string;
  nci_elimination: string;
  contribution: string;
}

export interface ConsolidationReviewMatrixRow {
  group_account_id: number;
  code: string;
  name: string;
  parent_id: number | null;
  sort_order: number;
  account_type: string;
  is_balance_sheet: boolean;
  section_break: string | null;
  by_entity: Record<string, ConsolidationReviewMatrixRowEntityValues>;
  consolidated: string;
}

export interface ConsolidationReviewMatrix {
  title: string;
  period_id: number;
  period_label: string;
  scenario: string;
  reporting_currency: string;
  run_id: number;
  pov: {
    data_source: string;
    intercompany: string;
    movement: string;
    rate_basis: string;
  };
  column_groups: ConsolidationReviewMatrixColumnGroup[];
  final_column: { key: string; label: string };
  entities: ConsolidationReviewMatrixEntity[];
  rows: ConsolidationReviewMatrixRow[];
}

export interface ConsolidationReviewSummaryCompany {
  id: number;
  code: string;
  name: string;
}

export interface ConsolidationReviewSummaryRun {
  run_id: number;
  completed_at: string | null;
  entity_count: number;
  is_large: boolean;
  companies: ConsolidationReviewSummaryCompany[];
  hidden_company_count: number;
}

export interface ConsolidationReviewSummary {
  period_id: number;
  period_label: string;
  total_runs: number;
  unique_company_count: number;
  runs: ConsolidationReviewSummaryRun[];
}

// --- Entity Type Lookup -------------------------------------------------------

export interface EntityTypeLookup {
  id: number;
  tenant_id: number;
  code: string;
  name: string;
  description: string | null;
  is_consolidating: boolean;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface EntityTypeLookupCreate {
  code: string;
  name: string;
  description?: string;
  is_consolidating: boolean;
  sort_order: number;
  is_active: boolean;
}

export interface EntityStatusLookup {
  id: number;
  tenant_id: number;
  code: string;
  name: string;
  description: string | null;
  allows_tb_upload: boolean;
  allows_consolidation: boolean;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface EntityStatusLookupCreate {
  code: string;
  name: string;
  description?: string;
  allows_tb_upload: boolean;
  allows_consolidation: boolean;
  sort_order: number;
  is_active: boolean;
}

// --- Consolidation Method Lookup ---------------------------------------------

export interface ConsolidationMethodLookup {
  id: number;
  tenant_id: number;
  code: string;
  name: string;
  description: string | null;
  ownership_min: number | null;
  ownership_max: number | null;
  lower_operator: string;
  upper_operator: string;
  range_on: boolean;
  is_system_method: boolean;
  default_control: boolean;
  default_consolidation_pct: number;
  requires_control: boolean | null;
  consolidation_pct_rule: string | null;
  nci_applicable: boolean;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ConsolidationMethodLookupCreate {
  code: string;
  name: string;
  description?: string;
  ownership_min?: number;
  ownership_max?: number;
  lower_operator?: string;
  upper_operator?: string;
  range_on?: boolean;
  default_control?: boolean;
  default_consolidation_pct?: number;
  requires_control?: boolean;
  consolidation_pct_rule?: string;
  nci_applicable: boolean;
  sort_order: number;
  is_active: boolean;
}

// --- Entity Hierarchy --------------------------------------------------------

export interface EntityHierarchy {
  id: number;
  tenant_id: number;
  code: string;
  name: string;
  description: string | null;
  hierarchy_type: string | null;
  is_primary: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string | null;
}

export interface EntityHierarchyCreate {
  code: string;
  name: string;
  description?: string;
  is_primary?: boolean;
}

export interface HierarchyTreeNode {
  id: number;
  entity_id: number;
  entity_code: string | null;
  entity_name: string | null;
  parent_entity_id: number | null;
  level: number;
  display_order: number;
  children: HierarchyTreeNode[];
}

export interface HierarchyNodeCreate {
  entity_id: number;
  parent_entity_id?: number | null;
  display_order?: number;
}

// --- Consolidation Status ----------------------------------------------------

export interface EntityConsolidationStatusItem {
  id: number;
  entity_id: number;
  entity_code: string | null;
  entity_name: string | null;
  period_id: number;
  scenario: string;
  tb_loaded: boolean;
  tb_balanced: boolean;
  ic_matched: boolean;
  ownership_locked: boolean;
  fx_rates_available: boolean;
  journals_posted: boolean;
  consolidation_status: 'ready' | 'not_ready';
  last_checked_at: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface ConsolidationStatusSummary {
  period_id: number;
  scenario: string;
  total_entities: number;
  ready_count: number;
  partial_count: number;
  not_ready_count: number;
  consolidated_count: number;
  entities: EntityConsolidationStatusItem[];
}

// --- TB Lock -----------------------------------------------------------------

export interface TBLockResponse {
  id: number;
  entity_id: number;
  period_id: number;
  scenario: string;
  lock_status: 'unlocked' | 'locked' | 'submitted' | 'approved';
  locked_by: number | null;
  locked_at: string | null;
  submitted_by: number | null;
  submitted_at: string | null;
  approved_by: number | null;
  approved_at: string | null;
  comment: string | null;
  rejected_by: number | null;
  rejected_at: string | null;
}

// --- Menu Configuration ------------------------------------------------------

export interface MenuConfigItem {
  id: number;
  tenant_id: number;
  menu_key: string;
  is_visible: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface MenuConfigListResponse {
  items: MenuConfigItem[];
  total: number;
}

export interface MenuConfigUpdate {
  menu_key: string;
  is_visible: boolean;
  sort_order: number;
}

// ─── Jobs Module ────────────────────────────────────────────────────────────

export type JobStatus = 'pending' | 'running' | 'completed' | 'completed_with_warnings'
  | 'completed_with_errors' | 'completed_with_threshold' | 'completed_with_rule_errors' | 'failed'
  | 'pending_cancel' | 'cancelled' | 'unknown';
export type JobCategory = 'consolidation' | 'import' | 'export' | 'validation' | 'intercompany' | 'reporting' | 'system';
export type JobType = 'consolidation' | 'single_skill' | 'refresh_database'
  | 'import_data' | 'import_metadata' | 'export' | 'export_metadata'
  | 'detection' | 'data_quality' | 'ic_matching'
  | 'xbrl_generation' | 'system_maintenance' | 'integration_pipeline';

export interface Job {
  id: number;
  job_name: string | null;
  task_id: string;
  task_type: string;
  job_category: string | null;
  status: JobStatus;
  progress_percent: number;
  current_step: string | null;
  total_steps: number;
  parameters: Record<string, unknown> | null;
  result_summary: Record<string, unknown> | null;
  result_path: string | null;
  triggered_by: string | null;
  priority: number;
  error_message: string | null;
  user_id: number | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface JobListResponse {
  items: Job[];
  total: number;
}

export interface JobStats {
  total: number;
  by_status: Record<string, number>;
  by_type: Record<string, number>;
  avg_runtime_seconds: number;
}

// ─── Job Schedules ──────────────────────────────────────────────────────────

export interface JobSchedule {
  id: number;
  tenant_id: number;
  created_by_id: number | null;
  job_name: string;
  job_type: string;
  parameters: Record<string, unknown> | null;
  is_active: boolean;
  recurrence_type: 'once' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly';
  recurrence_interval: number;
  scheduled_start: string | null;
  scheduled_end: string | null;
  timezone: string;
  cron_expression: string | null;
  last_run_at: string | null;
  next_run_at: string | null;
  total_runs: number;
  total_failures: number;
  created_at: string;
  updated_at: string;
}

export interface ScheduleListResponse {
  items: JobSchedule[];
  total: number;
}

// --- Smart Lists -------------------------------------------------------------

export interface SmartList {
  id: number;
  tenant_id: number;
  name: string;
  description: string | null;
  display_order: 'id' | 'name' | 'label';
  missing_dropdown_label: string | null;
  missing_form_label: 'dropdown_setting' | 'form_setting';
  auto_generate_id: boolean;
  is_active: boolean;
  is_system: boolean;
  entry_count: number;
  created_at: string;
  updated_at: string;
}

export interface SmartListEntry {
  id: number;
  smart_list_id: number;
  code: string;
  label: string;
  numeric_value: number | null;
  sort_order: number;
  is_active: boolean;
}

export interface SmartListWithEntries extends SmartList {
  entries: SmartListEntry[];
}

export interface SmartListCreate {
  name: string;
  description?: string;
  display_order?: 'id' | 'name' | 'label';
  missing_dropdown_label?: string;
  missing_form_label?: 'dropdown_setting' | 'form_setting';
  auto_generate_id?: boolean;
  entries?: Omit<SmartListEntry, 'id' | 'smart_list_id' | 'is_active'>[];
}

export interface SmartListUsage {
  dimension_type: string;
  member_id: number;
  member_code: string;
  member_name: string;
}

// ── UDAs (User-Defined Attributes) ──────────────────────────────────────────

export interface UdaDefinition {
  id: number;
  tenant_id: number;
  dimension_type: string;
  name: string;
  description: string | null;
  is_system: boolean;
  is_active: boolean;
  member_count: number;
  created_at: string;
  updated_at: string;
}

export interface UdaDefinitionCreate {
  dimension_type: string;
  name: string;
  description?: string;
}

export interface UdaDefinitionWithMembers extends UdaDefinition {
  members: UdaMemberItem[];
}

export interface UdaMemberItem {
  member_id: number;
  member_code: string;
  member_name: string;
}
